'use client';

import { messagePlainText } from '@shared/all/contentFilter/contentFilter';
import { buildAvatarUrl } from '@shared/all/helpers/avatarUrl';
import { API_URL } from '@shared/utils/helpers/config';
import { getS3ObjectURL } from '@shared/utils/helpers/s3';
import toast from '@shared/utils/helpers/toast';
import useAuth from '@shared/utils/hooks/useAuth';
import { useConversationActions } from '@shared/utils/hooks/useConversations';
import useFactiiis from '@shared/utils/hooks/useFactiiis';
import useNavigator from '@shared/utils/hooks/useNavigator';
import useRealtimeBridge from '@shared/utils/hooks/useRealtimeBridge';
import useSocket from '@shared/utils/hooks/useSocket';
import { useSocketAuthRecovery } from '@shared/utils/hooks/useSocketAuthRecovery';
import useUser from '@shared/utils/hooks/useUser';
import { UNREAD_MESSAGES_KEY } from '@shared/utils/queryKeys';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { type PropsWithChildren, startTransition, useEffect, useRef, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import eventEmitter from '../../helpers/eventEmitter';
import { TERMINAL_WINDOW_PATH } from '../../helpers/terminalWindow';
import { useLocationPathname, useLocationSearchParam } from '../../hooks/useLocationSearchParam';
import usePresence from '../../hooks/usePresence';
import { useShouldShowOnboardingOverlay } from '../../utils/onboardingStorage';
import { client, service } from '../../utils/trpc';
import { VerificationSessionScreen } from '../verification/SessionScreen';
import DdosGate from './DdosGate';
import OnboardingGate from './OnboardingGate';
import { Splash } from './Splash';

// Upper bound on the loading splash. Not the normal dismissal path — see the
// safety-net effect in Main.
const SPLASH_MAX_MS = 8000;

// The chat workspace (rail, DM/channel sidebar, thread) — rendered globally so
// it works on all pages, and so going full screen keeps the same mount.
const ChatWorkspace = dynamic(() => import('../pages/chat/ChatWorkspace'), {
  ssr: false
});
export const Main: React.FC<PropsWithChildren> = ({
  children
}) => {
  // Not useSearchParams: that would make the root layout's Suspense boundary
  // a dynamic hole and push every page's content out of the prerendered HTML
  // (see useLocationSearchParam). The server and the hydrating render see no
  // mode, so an embed WebView shows the splash until hydration, then drops
  // the chrome on the very next render.
  const mode = useLocationSearchParam('mode');
  const isEmbed = mode === 'embed' || mode === 'compact' || mode === 'mobile';
  // A window, not a page: no chat rail or popups over a shell you type into.
  // Keeps the toaster — that is where its errors go. Read from the window,
  // not usePathname: the terminal is only ever opened by window.open, never
  // reached by a soft navigation, and a usePathname here kept the whole shell
  // request-bound on every dynamic route.
  const isWindow = useLocationPathname() === TERMINAL_WINDOW_PATH;
  const [splash, setSplash] = useState<'loading' | 'maintenance' | null>('loading');
  // Keeps the splash mounted through its fade-out (was AnimatePresence). By
  // then `splash` is already null, so the last mode is retained for the
  // outgoing frame.
  const splashPresence = usePresence(splash !== null, 500);
  // Whether any query has actually been in flight yet — see the splash effect.
  const startedFetching = useRef(false);
  const lastSplashMode = useRef(splash);
  if (splash) lastSplashMode.current = splash;
  const splashMode = splash ?? lastSplashMode.current;
  const isFetching = useIsFetching();
  const queryClient = useQueryClient();
  // Actions only: Main subscribes to no conversation state, so a typing event
  // or a new message does not rerender the whole shell.
  const {
    resetConversations
  } = useConversationActions();
  const {
    currentUser,
    initializeUser,
    refreshUserFromCookie,
    logout
  } = useAuth({
    client,
    service,
    onLogout: resetConversations
  });
  // Same predicate OnboardingGate renders on, so the floating widgets hide only
  // while the overlay is actually up (embed mode already early-returns below).
  const isOnboarding = useShouldShowOnboardingOverlay(currentUser);
  const {
    setIsOnline
  } = useNavigator();
  const {
    initializeFactiiis
  } = useFactiiis(client);
  const {
    setIsAuthenticated
  } = useUser();
  const {
    onOnedriveChanged,
    registerOnedriveWebhook,
    mintEmbedToken
  } = useSocket();

  // Non-socket initialization
  useEffect(() => {
    const eventLogoutListener = () => {
      logout(true);
    };
    const cookieRotatedListener = () => {
      void refreshUserFromCookie();
    };
    const onlineListener = () => setIsOnline(true);
    eventEmitter.on('logout', eventLogoutListener);
    eventEmitter.on('setOnline', onlineListener);
    eventEmitter.on('userCookieRotated', cookieRotatedListener);

    // Every first render starts signed-out-unknown (Provider seeds no user, so
    // SSR and hydration agree). A fresh client cookie is the same answer the
    // server would give, so it is applied here without a round trip — the
    // state the cookie-seeded first render used to start in. Only a missing or
    // unreadable cookie asks the server.
    //
    // As a transition: this runs before the streamed Suspense boundaries (the
    // header, a page's content) have hydrated. An urgent update would make
    // React hydrate them against the signed-in state and throw a hydration
    // mismatch ("your-spaces" vs "suggested", avatar img vs placeholder); a
    // transition lets them hydrate as rendered first, then applies the user.
    // On web refreshUserFromCookie dispatches synchronously, so the outer
    // transition covers it; the inner one covers the update after the await.
    // The transition alone did not hold: a boundary that hydrated after it
    // still rendered signed in. `useUserState` (shared/utils/context/
    // UserContext.tsx) now gives every hydrating render the provider's
    // starting state, so hydration matches whatever order this lands in.
    if (currentUser.isAuthenticated === null) {
      startTransition(() => {
        void refreshUserFromCookie().then(fromCookie => {
          startTransition(() => {
            if (fromCookie) setIsAuthenticated(true);else void initializeUser();
          });
        });
      });
    }
    void initializeFactiiis();
    return () => {
      eventEmitter.off('logout', eventLogoutListener);
      eventEmitter.off('setOnline', onlineListener);
      eventEmitter.off('userCookieRotated', cookieRotatedListener);
    };
  }, []);

  // The handshake rejects a stale token now, and socket.io calls a middleware
  // error fatal, so something has to refresh and reconnect. Shared with mobile.
  useSocketAuthRecovery(client);

  // The conversation list, the socket and everything it carries, shared with
  // mobile. Anonymous visitors connect too: the server lets any socket join
  // `post-<id>` rooms (joinPostStats), which is how a logged-out reader sees
  // live vote counts. What follows is only what the desktop and web add.
  useRealtimeBridge({
    client,
    service,
    isAuthenticated: currentUser.isAuthenticated,
    currentUserId: currentUser.id,
    initializeUser,
    logout: () => logout(true),
    showServerMessage: message => toast(message),
    showTronReward: (quantity, title) => toast(`You earned ${quantity.toLocaleString()} Trons for: ${title}!`),
    onNotificationCount: (count, detail) => {
      // Native Electron notification
      if (window.electronAPI?.isElectron) {
        window.electronAPI.setBadgeCount(count);
        if (count > 0 && !document.hasFocus() && detail) {
          const messages: Record<string, string> = {
            UPVOTED: `${detail.username} upvoted your post`,
            FOLLOWED: `${detail.username} followed you`,
            REPLIED: `${detail.username} replied to your post`,
            TAGGED_POST: `${detail.username} tagged you in a post`,
            SPACE_INVITES: `${detail.username} invited you to a space`,
            BOARD_POST: `${detail.username} added a card to a board`,
            SPACE_BANNED: `You were banned from a space`,
            SPACE_DELISTED: `Your space was delisted`,
            FACTIII_MODERATION_REQUIRED: `Your factiii needs moderation`,
            FACTIII_MODERATION_APPROVED: `Your factiii was approved`,
            FACTIII_MODERATION_REJECTED: `Your factiii was rejected`
          };
          const body = messages[detail.type] ?? `You have a new notification`;
          window.electronAPI.showNotification('Factiii', body);
        }
      }
    },
    onNewMessage: (sender, _convId, message_0) => {
      // A conversation this account has not loaded can still go unread, so
      // the header's badge is re-counted server-side rather than derived
      // from the conversations already in context.
      void queryClient.invalidateQueries({
        queryKey: UNREAD_MESSAGES_KEY
      });
      // Native Electron notification for new messages with avatar.
      //
      // `sender.id` is checked because the server broadcasts `newMessage` to
      // the WHOLE conversation room with no `.except()` — deliberately, since
      // this account's other devices need it to keep their transcript in
      // sync. Without the check, sending from the phone popped a desktop
      // notification announcing your own message back to you.
      if (window.electronAPI?.isElectron && !document.hasFocus() && sender.id !== currentUser.id) {
        // A raster, because this ends up as an OS notification icon — macOS
        // and Windows both want a bitmap, not a vector.
        const avatarUrl = sender.avatarKey ? getS3ObjectURL(sender.avatarKey) : sender.robohash ? buildAvatarUrl(API_URL, sender.robohash, {
          format: 'png',
          size: 128
        }) : undefined;
        window.electronAPI.showNotification(sender.name || sender.username,
        // Plain text: the stored message carries tags and marks.
        messagePlainText(message_0, {
          censor: true
        }), avatarUrl);
      }
    },
    connectExtras: () => {
      // OneDrive index push (desktop only): relay change pokes and mint webhooks
      // for the shell. Gate on the namespace, not on isElectron - shells older
      // than 1.6.0 don't expose `onedrive` and would crash the app here.
      let cleanupOnedrive = () => {};
      if (window.electronAPI?.onedrive) {
        const api = window.electronAPI.onedrive;
        const offChanged = onOnedriveChanged(spaceSlug => api.changed(spaceSlug));
        const offMint = api.onMintRequest(spaceSlug_0 => {
          void registerOnedriveWebhook(spaceSlug_0).then(reg => {
            // Callback URL is the API origin this app talks to; Graph must be able
            // to reach it, so a localhost dev origin simply never gets a poke and
            // the index falls back to its delta pass.
            const base = process.env.NEXT_PUBLIC_API_URL;
            if (!reg || !base) return;
            api.registerWebhook({
              spaceSlug: spaceSlug_0,
              notificationUrl: `${base.replace(/\/$/, '')}/onedrive/webhook`,
              clientState: reg.clientState
            });
          });
        });
        // The shell's index service embeds through the server and needs a token
        // for it; only this socket can mint one. A shell from before the token
        // never asks. No answer on a failed mint: the shell asks again.
        const offEmbedToken = api.onEmbedTokenRequest?.(() => {
          void mintEmbedToken().then(token => {
            if (token) api.embedToken?.(token);
          });
        });
        cleanupOnedrive = () => {
          offChanged();
          offMint();
          offEmbedToken?.();
        };
      }
      return cleanupOnedrive;
    }
  });

  // Safety net only: every real load fires at least one query, so the effect
  // below dismisses long before this. It exists so a pathological load where
  // nothing ever fetches can't strand the user on the splash forever.
  useEffect(() => {
    const timer = setTimeout(() => {
      startedFetching.current = true;
      setSplash(prev => prev === 'loading' ? null : prev);
    }, SPLASH_MAX_MS);
    return () => clearTimeout(timer);
  }, []);

  // Splash: hide when loaded, switch to maintenance if server is unreachable
  useEffect(() => {
    // `isFetching === 0` is true in two opposite situations: after everything
    // settles, and before anything starts. At boot it is the second — every
    // query waits on auth.refresh, which is a raw client call rather than a
    // React Query one and so never registers here. Dismissing on that zero
    // dropped the splash mid-flight and left a bare shell on screen until the
    // batch landed, so the counter has to rise once before zero means "done".
    if (isFetching > 0) startedFetching.current = true;
    if (currentUser.isAuthenticated !== null && isFetching === 0 && startedFetching.current) {
      // Dismiss splash whether it's in loading or maintenance (recovered)
      setSplash(prev_0 => prev_0 ? null : prev_0);
    }
    const handleOffline = () => {
      if (splash === 'loading') {
        setSplash('maintenance');
      } else {
        setIsOnline(false);
      }
    };

    // Recover from maintenance splash when server comes back online
    const handleOnline = () => {
      setSplash(prev_1 => prev_1 === 'maintenance' ? null : prev_1);
    };
    eventEmitter.on('setOffline', handleOffline);
    eventEmitter.on('setOnline', handleOnline);
    return () => {
      eventEmitter.off('setOffline', handleOffline);
      eventEmitter.off('setOnline', handleOnline);
    };
  }, [currentUser.isAuthenticated, isFetching, splash]);
  if (isEmbed) return <>{children}</>;
  return <>
      {splashPresence.rendered && splashMode && <Splash mode={splashMode} exiting={splashPresence.exiting} onRetry={splashMode === 'maintenance' ? () => window.location.reload() : undefined} />}
      <DdosGate />
      <OnboardingGate />
      {children}
      {!isOnboarding && !isWindow && <ChatWorkspace />}
      <VerificationSessionScreen />
      <Toaster position="bottom-center" toastOptions={{
      duration: 3000,
      style: {
        fontFamily: 'Lato, sans-serif',
        fontSize: '15px',
        borderRadius: '10px',
        maxWidth: '420px',
        textAlign: 'left',
        background: 'rgb(var(--background-secondary-color))',
        color: 'rgb(var(--text-color))',
        border: 'none',
        boxShadow: '0 16px 48px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(var(--border-color), 0.12)',
        padding: '14px 20px',
        whiteSpace: 'pre-line'
      },
      success: {
        iconTheme: {
          primary: 'rgb(var(--primary))',
          secondary: 'rgb(var(--primary-foreground))'
        }
      },
      error: {
        iconTheme: {
          primary: 'rgb(var(--destructive))',
          secondary: 'rgb(var(--destructive-foreground))'
        }
      },
      loading: {
        iconTheme: {
          primary: 'rgb(var(--muted-foreground))',
          secondary: 'rgb(var(--muted))'
        }
      }
    }} />
    </>;
};